/**
 * Calm Guidance V1.5 — Instructor-side struggle follow-up tile.
 *
 * ONE small calm operational tile that appears at the top of
 * PupilProfile when the V1 learner-side struggle rule has emitted
 * an active signal for this pupil.
 *
 * V1.6 closure synchronisation (Feb 28, 2026): when the instructor
 * taps either action, a fire-and-forget POST to the EXISTING
 * /api/learner-state/dismiss endpoint (with
 * `on_behalf_of_pupil_id`) clears the learner-side card on their
 * next /learn load. No new endpoint, no new collection, no
 * acknowledgement ceremony — just the same dismissal substrate
 * used for learner self-dismiss.
 *
 * Doctrine:
 *   • Not analytics. Not a coaching panel.
 *   • Operational memory only.
 *   • Silent when /api/instructor/struggle-context/{pupilId}
 *     returns `{active: false}` — naturally decays.
 *   • Reuses existing routes (/learn/hub/:slug + /calendar-planner)
 *     and existing dismiss endpoint only.
 *   • No percentages, scores, risk ratings, "AI recommends".
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// V1.6 closure-sync — fires the existing dismiss endpoint.
// Returns a promise that resolves regardless of failure so the
// caller can always navigate. Calm + invisible by design.
function closeStruggleOnLearnerSide({ signalKey, pupilId }) {
  if (!signalKey || !pupilId) return Promise.resolve();
  return axios
    .post(
      `${API}/api/learner-state/dismiss`,
      {
        signal_key: signalKey,
        slot: "focus_today",
        on_behalf_of_pupil_id: pupilId,
      },
      { withCredentials: true },
    )
    .catch(() => {
      // Closure-sync is best-effort. Failure must NEVER block the
      // instructor's primary navigation. The learner-side card
      // will decay naturally on the next recurrence-truth check.
    });
}

export default function StruggleFollowUpTile({ pupilId }) {
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    if (!pupilId) return;
    let cancelled = false;
    axios
      .get(`${API}/api/instructor/struggle-context/${pupilId}`, {
        withCredentials: true,
      })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        if (data.active) setCtx(data);
        else setCtx(null);
      })
      .catch(() => {
        if (!cancelled) setCtx(null);
      });
    return () => {
      cancelled = true;
    };
  }, [pupilId]);

  if (!ctx) return null;

  // Role-safety guard (Feb 28, 2026 — Operational Spine Hardening
  // Batch 2D P0 follow-up). The tile mounts on /pupil/:pupilId
  // (allowedRoles=[adi, pdi, admin]). The backend currently emits
  // primary_action.target = `/learn/hub/{slug}` which is gated to
  // [learner, admin] — so ADI/PDI taps would redirect to
  // homeForRole(effectiveRole). Suppress any primary action whose
  // target points into the learner surface graph. The secondary
  // action (/calendar-planner) is instructor-reachable and stays
  // intact.
  const primaryAction =
    ctx.primary_action && !String(ctx.primary_action.target || "").startsWith("/learn/")
      ? ctx.primary_action
      : null;

  const handleAction = (target) => (e) => {
    if (!target) return;
    e.preventDefault();
    // V1.6 closure sync — kick off the dismissal, then navigate.
    // Both can happen in parallel; the user does not wait on the
    // dismiss round-trip.
    closeStruggleOnLearnerSide({
      signalKey: ctx.signal_key,
      pupilId,
    });
    window.location.assign(target);
  };

  return (
    <section
      data-testid="struggle-followup-tile"
      className="rounded-2xl ring-1 ring-amber-200 bg-amber-50/60 dark:bg-amber-900/10 dark:ring-amber-900/40 p-4"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-xl bg-amber-100 text-amber-700 inline-flex items-center justify-center">
          <Sparkles className="w-4 h-4" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
            Operational memory
          </p>
          <p
            data-testid="struggle-followup-headline"
            className="font-bold text-sm text-slate-900 dark:text-white mt-0.5"
          >
            {ctx.headline}
          </p>
          <p
            data-testid="struggle-followup-body"
            className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-snug"
          >
            {ctx.body}
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {primaryAction && (
              <a
                data-testid="struggle-followup-primary-action"
                href={primaryAction.target}
                onClick={handleAction(primaryAction.target)}
                className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-slate-900 ring-1 ring-amber-200 dark:ring-amber-900/40 px-3 py-1.5 text-xs font-bold text-slate-900 dark:text-white hover:shadow-sm transition"
              >
                {primaryAction.label}
                <ChevronRight className="w-3.5 h-3.5 text-amber-500" aria-hidden />
              </a>
            )}
            {ctx.secondary_action && (
              <a
                data-testid="struggle-followup-secondary-action"
                href={ctx.secondary_action.target}
                onClick={handleAction(ctx.secondary_action.target)}
                className="inline-flex items-center gap-1 rounded-full bg-transparent ring-1 ring-amber-200 dark:ring-amber-900/40 px-3 py-1.5 text-xs font-bold text-amber-700 hover:bg-amber-100/40 dark:hover:bg-amber-900/20 transition"
              >
                {ctx.secondary_action.label}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
